import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AthletePRsTable from './AthletePRsTable';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { getAthletePrReport, getAthletePrReportFilters } from 'api/reportsApi';
import MultiSelectFilter from 'components/admin/MultiSelectFilter';
import filterStyles from 'components/admin/bulkAdminTable.module.css';
import useGroupLabels from 'hooks/useGroupLabels';
import useSessionFilters from 'hooks/useSessionFilters';
import { activeAgeFilter } from 'utils/ageFilter';
import { formatGroupRange } from 'utils/groupLabel';
import styles from './AthletePRs.module.css';

/**
 * Parse age group range string into array of ages
 * Examples:
 * - "U5" → [5]
 * - "U13-17" → [13, 14, 15, 16, 17]
 * - "U14-U17" → [14, 15, 16, 17]
 */
const parseAgeRange = (ageRange) => {
  if (!ageRange) return [];
  
  // Remove 'U' prefix and handle ranges
  const cleaned = ageRange.replace(/U/g, '');
  
  if (cleaned.includes('-')) {
    const [start, end] = cleaned.split('-').map(Number);
    const ages = [];
    for (let i = start; i <= end; i++) {
      ages.push(i);
    }
    return ages;
  }
  
  return [Number(cleaned)];
};

const GENDER_OPTIONS = [
  { value: 'all', label: 'All genders' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

/**
 * The club's groups from the filters API, in the club's own order. Each keeps
 * its stored range ("U13-17") as its value, and the ages that range covers.
 */
const buildGroups = (ageGroupsFromApi) => {
  if (!ageGroupsFromApi || ageGroupsFromApi.length === 0) return [];

  return ageGroupsFromApi
    .map((group) => ({
      value: group.ageRange,
      ages: parseAgeRange(group.ageRange),
      sortOrder: group.sortOrder,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
};

const AthletePRs = ({ clubID, onDataChange }) => {
  // The group filter reads Age Group and "U10-U11", or Year Group and "Y5-Y6" for a club that groups by
  // year level. A choice keeps its stored range as its value; only what it shows changes.
  const { groupingBasis, words } = useGroupLabels(clubID);

  // Filters, kept for the tab's session. `groups` is the ticked ranges; empty means every group.
  // `eventCodes` is the ticked events, as strings; empty means every event, and null (never chosen)
  // the club's first event. `seasonBests` is 'all', or 'club' for only those that scored season-best points.
  const [filters, setFilter] = useSessionFilters(
    `athletePRsReportFilters_${clubID}`,
    { eventCodes: null, groups: [], gender: 'all', seasonBests: 'all' }
  );
  const { gender: genderFilter, seasonBests } = filters;
  const pointsEligibleOnly = seasonBests === 'club';

  // Filter options from the API
  const [eventTypes, setEventTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Report data: the season bests loaded so far, by event code, and the events asked for whose answer
  // is not in yet. Both are for the club and season bests choice in force.
  const [rowsByEvent, setRowsByEvent] = useState({});
  const [pendingCodes, setPendingCodes] = useState([]);

  // The events asked for so far, and the club and season bests choice they were asked for, so an event
  // is asked for once and an answer for an earlier choice is dropped.
  const requestedRef = useRef(new Set());
  const choiceRef = useRef('');

  const [loadingFilters, setLoadingFilters] = useState(false);

  // The club the event list was read for. After a club switch the list is still the old club's until
  // the new one arrives, and no season bests are asked for until then (Doug, 2026-10-25: the page asked
  // for the new club's season bests with the old club's events). The ref drops a list that arrives for
  // a club the page has already left.
  const [eventTypesClubID, setEventTypesClubID] = useState(null);
  const clubRef = useRef(clubID);
  clubRef.current = clubID;

  // The Event filter's choices, in the club's order.
  const eventOptions = useMemo(
    () =>
      eventTypes.map((event) => ({
        value: String(event.eventCode),
        label: event.displayName,
      })),
    [eventTypes]
  );

  // The ticked events in force. Empty is "All events". The first visit ticks the club's first event, as
  // the page did when it showed one event at a time (Doug, 2026-10-25); so does a remembered choice the
  // club no longer has any of. A remembered event the club no longer has is dropped.
  const eventFilter = useMemo(() => {
    if (eventOptions.length === 0) return [];
    if (Array.isArray(filters.eventCodes)) {
      if (filters.eventCodes.length === 0) return [];
      const offered = new Set(eventOptions.map((o) => o.value));
      const kept = filters.eventCodes.filter((code) => offered.has(code));
      if (kept.length > 0) return kept;
    }
    return [eventOptions[0].value];
  }, [eventOptions, filters.eventCodes]);

  // The ticked events in the club's order, or every one for "All events": what the table shows and
  // what Export to file asks for. One string, so a new array with the same events is the same choice.
  const eventCodesKey = useMemo(() => {
    const ticked = new Set(eventFilter);
    return eventOptions
      .filter((o) => eventFilter.length === 0 || ticked.has(o.value))
      .map((o) => o.value)
      .join(',');
  }, [eventOptions, eventFilter]);

  const groupOptions = useMemo(
    () =>
      groups.map((group) => ({
        value: group.value,
        label: formatGroupRange(groupingBasis, group.value),
      })),
    [groups, groupingBasis]
  );

  // The ticked groups in force: a remembered one the club no longer has is dropped.
  const groupFilter = useMemo(
    () => activeAgeFilter(filters.groups, groupOptions),
    [filters.groups, groupOptions]
  );

  // The ages the ticked groups cover, in order and without repeats. Empty means every age.
  const selectedAges = useMemo(() => {
    const ages = new Set();
    groupFilter.forEach((value) => {
      const group = groups.find((g) => g.value === value);
      if (group) group.ages.forEach((age) => ages.add(age));
    });
    return [...ages].sort((a, b) => a - b);
  }, [groupFilter, groups]);

  // Fetch filter options from API. Once for the club: choosing an event does not ask again.
  const fetchFilters = useCallback(async () => {
    try {
      setLoadingFilters(true);
      const response = await getAthletePrReportFilters(clubID);
      if (clubRef.current !== clubID) return; // the page has moved to another club

      const { eventTypes: eventsFromApi, ageGroups: ageGroupsFromApi } = response.data;

      setEventTypes(eventsFromApi || []);
      setGroups(buildGroups(ageGroupsFromApi || []));
      setEventTypesClubID(clubID);

      setLoadingFilters(false);
    } catch (err) {
      console.error('[Athlete PR Report] Fetch filters error: ', err);
      toast.error('Failed to load filter options');
      setLoadingFilters(false);
    }
  }, [clubID]);

  // Initial load of filters
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Another club or season bests choice starts again: nothing loaded, nothing asked for. Declared before
  // the effect below, so it runs first when the choice changes.
  useEffect(() => {
    choiceRef.current = `${clubID}|${pointsEligibleOnly}`;
    requestedRef.current = new Set();
    setRowsByEvent({});
    setPendingCodes([]);
  }, [clubID, pointsEligibleOnly]);

  // Fetch report data for the ticked events not asked for yet: every group and both genders of each,
  // filtered here. The page asks for no more than it did when it showed one event at a time (Doug,
  // 2026-10-25): the first visit asks for the club's first event; ticking an event asks for that event;
  // unticking one, or ticking one already loaded, asks nothing; "All events" asks once for the rest.
  useEffect(() => {
    if (!eventCodesKey || eventTypesClubID !== clubID) return;
    const missing = eventCodesKey
      .split(',')
      .map(Number)
      .filter((code) => !requestedRef.current.has(code));
    if (missing.length === 0) return;

    const choice = choiceRef.current;
    missing.forEach((code) => requestedRef.current.add(code));
    setPendingCodes((prev) => [...prev, ...missing]);

    (async () => {
      try {
        const response = await getAthletePrReport({
          clubId: clubID,
          ageGroupAges: [], // Get all age groups
          gender: 'Both',   // Get both genders
          eventTypeCodes: missing,
          pointsEligibleOnly,
        });
        if (choiceRef.current !== choice) return; // asked for an earlier choice

        // Each row carries its event's place in the club's order and its category (a track time or a
        // distance), for the table's order and its Result column. An event with no season best is
        // loaded too, with no rows.
        const eventPlace = new Map(eventTypes.map((event, index) => [event.eventCode, index]));
        const byEvent = Object.fromEntries(missing.map((code) => [code, []]));
        (response.data || []).forEach((p) => {
          if (!byEvent[p.eventTypeCode]) return;
          byEvent[p.eventTypeCode].push({
            ...p,
            athleteName: `${p.firstName} ${p.lastName}`,
            eventOrder: eventPlace.get(p.eventTypeCode) ?? eventTypes.length,
            eventCategory: eventTypes.find((event) => event.eventCode === p.eventTypeCode)?.category,
          });
        });
        setRowsByEvent((prev) => ({ ...prev, ...byEvent }));
      } catch (err) {
        console.error('[Athlete PR Report] Generate report error: ', err);
        toast.error('Failed to generate report');
        // Not loaded: the next tick of these events asks again.
        if (choiceRef.current === choice) {
          missing.forEach((code) => requestedRef.current.delete(code));
        }
      } finally {
        if (choiceRef.current === choice) {
          setPendingCodes((prev) => prev.filter((code) => !missing.includes(code)));
        }
      }
    })();
  }, [clubID, eventCodesKey, eventTypes, eventTypesClubID, pointsEligibleOnly]);

  const athletePoints = useMemo(() => Object.values(rowsByEvent).flat(), [rowsByEvent]);

  // The table waits only while none of the ticked events is loaded yet. Otherwise it shows what is
  // loaded, and a newly ticked event's rows join it when they arrive.
  const tickedCodes = eventCodesKey ? eventCodesKey.split(',').map(Number) : [];
  const tableLoading =
    pendingCodes.length > 0 && !tickedCodes.some((code) => code in rowsByEvent);

  // The rows the event, group, gender and search filters leave
  const filteredAthletePoints = useMemo(() => {
    const events = new Set(eventCodesKey.split(',').filter(Boolean).map(Number));
    const ages = new Set(selectedAges);
    const search = searchText.toLowerCase();
    return athletePoints.filter(
      (p) =>
        events.has(p.eventTypeCode) &&
        (ages.size === 0 || ages.has(p.age)) &&
        (genderFilter === 'all' || p.gender === genderFilter) &&
        (!search || p.athleteName.toLowerCase().includes(search))
    );
  }, [athletePoints, eventCodesKey, selectedAges, genderFilter, searchText]);

  // Tell the page what is showing, for its Export to file button. The file is asked for with the
  // same events, ages, gender and season bests choice.
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredAthletePoints.length > 0,
        eventTypeCodes: eventCodesKey ? eventCodesKey.split(',').map(Number) : [],
        ageGroupAges: selectedAges, // Unique numbers like [13, 14, 15, 16, 17]; empty for every age
        selectedGender: genderFilter === 'all' ? 'Both' : genderFilter,
        pointsEligibleOnly,
      });
    }
  }, [filteredAthletePoints, eventCodesKey, selectedAges, genderFilter, pointsEligibleOnly, onDataChange]);

  return (
    <LoadingContent loading={loadingFilters}>
      {/* Filter card: the same controls as the Start Lists page and the athlete lists. */}
      <div className={`${filterStyles.filterCard} ${styles.athletePRsFilterCard}`}>
        {/* Several events at once, as the group filter (Doug, 2026-10-25). */}
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>Event</label>
          <MultiSelectFilter
            value={eventFilter}
            options={eventOptions}
            onChange={(next) => setFilter('eventCodes', next)}
            allLabel="All events"
            itemLabel="event"
          />
        </div>
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>{words.group}</label>
          <MultiSelectFilter
            value={groupFilter}
            options={groupOptions}
            onChange={(next) => setFilter('groups', next)}
            allLabel={`All ${words.lower.groups}`}
            itemLabel={words.lower.group}
          />
        </div>
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>Gender</label>
          <select
            className={filterStyles.filterSelect}
            value={genderFilter}
            onChange={(e) => setFilter('gender', e.target.value)}
          >
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {/* Every season best, or only those that scored season-best points. */}
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>Season bests</label>
          <select
            className={filterStyles.filterSelect}
            value={seasonBests}
            onChange={(e) => setFilter('seasonBests', e.target.value)}
          >
            <option value="all">All season bests</option>
            <option value="club">Points-eligible season bests only</option>
          </select>
        </div>
      </div>

      {/* Only the table waits for the report, so the filters stay on the page while it loads. */}
      <LoadingContent loading={tableLoading}>
        <AthletePRsTable
          athletePoints={filteredAthletePoints}
          searchText={searchText}
          setSearchText={setSearchText}
        />
      </LoadingContent>
    </LoadingContent>
  );
};

AthletePRs.propTypes = {
  clubID: PropTypes.string.isRequired,
  onDataChange: PropTypes.func,
};

export default AthletePRs;
